import { Control, Controller } from 'react-hook-form';
import { Box, Radio, RadioGroup } from 'enova-frontend-components';
import { FC } from 'react';
import { useTranslation } from 'react-i18next';

import { AboutFormData } from '../../types';
import { ByggType, PlasseringAvEnhetIByggVertikalt } from '../../../../../utils/registerEnums';

interface Props {
  control: Control<AboutFormData>;
  options: PlasseringAvEnhetIByggVertikalt[];
  i18nPrefix: string;
}

export const VerticalPlacementRadio: FC<Props> = ({ control, options, i18nPrefix }) => {
  const { t } = useTranslation();

  return (
    <Controller
      name="bygginfo.verticalPlacement"
      control={control}
      rules={{
        validate: (value, formValues) => {
          // Vertical placement is only required in case of Tomannsbolig, Firemannsbolig, or Leilighet.
          // If the building type is not one of these, we skip validation
          if (
            formValues.bygginfo?.type !== ByggType.TomannsboligHorisontalDelt &&
            formValues.bygginfo?.type !== ByggType.Firemannsbolig &&
            formValues.bygginfo?.type !== ByggType.Leilighet
          ) {
            return true;
          }

          if (value === undefined || value === null) {
            return t(`register.errorMessage.field.vertikalPlassering`);
          }
          return true;
        }
      }}
      render={({ field: { value, onChange, ...rest }, fieldState: { error } }) => (
        <RadioGroup
          required
          label={t(`${i18nPrefix}.label`)}
          error={!!error}
          helperText={error && t(`${error.message}`)}
          value={value ?? null}
          onChange={(_, val) => onChange(val)}
          {...rest}
        >
          <Box display="flex" flexWrap="wrap" gap={2}>
            {options.map((option) => (
              <Radio key={option} value={option} label={t(`${i18nPrefix}.option.${option}`)} />
            ))}
          </Box>
        </RadioGroup>
      )}
    />
  );
};

export const VerticalPlacementLeilighet: FC<{
  control: Control<AboutFormData>;
}> = ({ control }) => {
  const i18nPrefix = 'register.bygningsdetaljer.leilighet.plasseringAvEnhetIByggVertikalt';

  const leilighetOptions = [
    PlasseringAvEnhetIByggVertikalt.OversteEtasje,
    PlasseringAvEnhetIByggVertikalt.MellomliggendeEtasje,
    PlasseringAvEnhetIByggVertikalt.NedersteEtasje
  ];

  return <VerticalPlacementRadio i18nPrefix={i18nPrefix} control={control} options={leilighetOptions} />;
};

export const VerticalPlacement: FC<{
  control: Control<AboutFormData>;
}> = ({ control }) => {
  const i18nPrefix = 'register.bygningsdetaljer.horisontaldeltBolig.plasseringAvEnhetIByggVertikalt';

  const tomannsboligOptions = [
    PlasseringAvEnhetIByggVertikalt.OversteEtasje,
    PlasseringAvEnhetIByggVertikalt.NedersteEtasje
  ];

  return <VerticalPlacementRadio i18nPrefix={i18nPrefix} control={control} options={tomannsboligOptions} />;
};
